import API from '@api';
import ProxyError from '@api/exceptions/ProxyError';
import {
	LEVEL_POTENTIAL,
	LEVEL_VIOLATION,
	PROXY_ERRORS,
	RATIO_EXCLUDED,
	RULE_TEXT_CONTRAST,
} from '@constants';
import { runAllEa11yRules } from '@rules';
import { sortViolations } from '@utils/sort-violations';

export default function useScannerWizardActions(state) {
	const {
		setPageHTML,
		pageURL,
		setPageURL,
		setProxyError,
		setIsError,
		setLoading,
		setOpenIndex,
		setSortedViolations,
		setResults,
	} = state;

	const handleOpen = (index) => (event, isExpanded) => {
		setOpenIndex(isExpanded ? index : null);
	};

	const handleOpenBlock = (block) => {
		state.setOpenedBlock(block);
		setOpenIndex(0);
	};

	const isViolation = (item) => item.level === LEVEL_VIOLATION;
	const isContrastViolation = (item) =>
		item.ruleId === RULE_TEXT_CONTRAST &&
		item.level === LEVEL_POTENTIAL &&
		Number(item.messageArgs[0]) !== RATIO_EXCLUDED;

	const savePageURL = () => {
		const fullURL = new URL(location.href);
		const scanPageURL = fullURL.searchParams.get('url');

		if (!scanPageURL) {
			console.log('Error in savePageURL(): Unable to retrieve a page URL');
			setIsError(true);

			return;
		}

		setPageURL(scanPageURL);
	};

	const rescanPage = (newURL) => {
		setProxyError(false);
		setIsError(false);

		setLoading(true);
		setResults(null);
		setSortedViolations(null);
		setPageHTML(null);

		if (newURL && newURL !== pageURL) {
			setPageURL(newURL);
		} else {
			fetchPage();
		}
	};

	const fetchPage = async () => {
		try {
			const response = await API.fetchPage(pageURL);
			const content = await response.text();

			setPageHTML(content);
		} catch (e) {
			console.error('Error in fetchPage(): ', e);

			if (e instanceof ProxyError) {
				setProxyError(e.proxyErrorCode);
			} else {
				setProxyError(PROXY_ERRORS.INTERNAL_SERVER_ERROR);
			}

			setLoading(false);
		}
	};

	const getResults = async (targetDocument) => {
		try {
			const data = await window.ace.check(targetDocument);

			const filtered = data.results.filter(
				(item) => isViolation(item) || isContrastViolation(item),
			);

			const customResults = runAllEa11yRules(targetDocument);

			const filteredCustomResults = customResults.filter((item) =>
				isViolation(item),
			);

			const allResults = [...filtered, ...filteredCustomResults];

			data.results = allResults;
			data.summary = {
				counts: {
					issuesResolved: 0,
					violation: allResults.filter(
						(item) => isViolation(item) || isContrastViolation(item),
					).length,
					recommendation: allResults.filter(
						(item) => item.level === 'recommendation',
					).length,
				},
			};

			const sorted = sortViolations(allResults);

			setResults(data);
			setSortedViolations(sorted);

			return data.summary;
		} catch (error) {
			console.error('Error in getResults(): ', error);
			setIsError(true);
		}
	};

	return {
		handleOpen,
		handleOpenBlock,
		savePageURL,
		rescanPage,
		fetchPage,
		getResults,
	};
}
